/**
 * Operator State — canonical admin state model for Home / Wizard / Diagnostics.
 *
 * Locked by: 10-decision-state-model-and-home-scope.md (2026-04-14)
 * Updated for 3-lane composition: 21-launch-implementation-plan.md (2026-04-15)
 *
 * This is the single source of truth for merchant-facing status.
 * All Home cards, wizard gates, and diagnostics derive from this shape.
 *
 * The OperatorState is composed from three lanes:
 *   1. Plugin-owned snapshot  (bridge)
 *   2. Gateway-owned settings (gateway/redux)
 *   3. Browser-local preview  (localStorage)
 *
 * See: loader/composeOperatorState.js for derivation rules.
 */

// ---------------------------------------------------------------------------
// State value enums
// ---------------------------------------------------------------------------

export const ConnectionStatus = Object.freeze({
  NOT_CONNECTED: 'not_connected',
  CONNECTED: 'connected',
  ERROR: 'error',
});

export const IndexingStatus = Object.freeze({
  IDLE: 'idle',
  RUNNING: 'running',
  READY: 'ready',
  ERROR: 'error',
});

export const StorefrontMode = Object.freeze({
  WIDGET: 'widget',
  CLASSIC: 'classic',
  COMBINED: 'combined',
});

// ---------------------------------------------------------------------------
// Preview: split into build (plugin-owned) and browser (localStorage)
// Doc 10 §1: previewBuild + previewBrowser are separate concerns.
// ---------------------------------------------------------------------------

export const PreviewBuildStatus = Object.freeze({
  UNKNOWN: 'unknown',
  NOT_READY: 'not_ready',
  BUILDING: 'building',
  READY: 'ready',
  ERROR: 'error',
});

/** @deprecated Use PreviewBuildStatus — kept for backward compat with existing consumers */
export const PreviewStatus = PreviewBuildStatus;

export const PreviewBrowserStatus = Object.freeze({
  UNKNOWN: 'unknown',
  DISABLED: 'disabled',
  ENABLED: 'enabled',
  STALE: 'stale',
  ERROR: 'error',
});

export const LiveStatus = Object.freeze({
  UNKNOWN: 'unknown',
  NOT_ACTIVE: 'not_active',
  ACTIVE: 'active',
  ERROR: 'error',
});

// ---------------------------------------------------------------------------
// Publish state (doc 48 §4) — tracks plugin-side publish lifecycle
// ---------------------------------------------------------------------------

export const PublishState = Object.freeze({
  NOT_PUBLISHED: 'not_published',
  PUBLISHED: 'published',
  UNPUBLISHED_CHANGES: 'unpublished_changes',
  BUILDING: 'building',
  ERROR: 'error',
});

export const IntegrationHealth = Object.freeze({
  HEALTHY: 'healthy',
  WARNING: 'warning',
  ERROR: 'error',
  UNKNOWN: 'unknown',
});

export const DiagnosticsSeverity = Object.freeze({
  NONE: 'none',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
});

// ---------------------------------------------------------------------------
// Next-action keys (priority order matches decision doc §6)
// ---------------------------------------------------------------------------

export const NextActionKey = Object.freeze({
  FIX_CONNECTION: 'fix_connection',
  CONNECT_STORE: 'connect_store',
  FIX_INDEXING: 'fix_indexing',
  START_INDEXING: 'start_indexing',
  WAIT_INDEXING: 'wait_indexing',
  CHOOSE_MODE: 'choose_mode',
  SWITCH_MODE: 'switch_mode',
  FIX_PREVIEW: 'fix_preview',             // deprecated alias — use FIX_PREVIEW_BUILD
  FIX_PREVIEW_BUILD: 'fix_preview_build',
  BUILD_PREVIEW: 'build_preview',
  WAIT_PREVIEW: 'wait_preview',            // deprecated alias — use WAIT_PREVIEW_BUILD
  WAIT_PREVIEW_BUILD: 'wait_preview_build',
  CHECK_PREVIEW: 'check_preview',
  REFRESH_PREVIEW: 'refresh_preview',
  FIX_LIVE: 'fix_live',
  GO_LIVE: 'go_live',                     // deprecated alias — use PUBLISH
  PUBLISH: 'publish',
  FIX_INTEGRATION: 'fix_integration',
  CHECK_INTEGRATION: 'check_integration',  // deprecated alias — use REVIEW_INTEGRATION
  REVIEW_INTEGRATION: 'review_integration',
});

// ---------------------------------------------------------------------------
// Symbolic route targets for nextAction.route
// Actual path construction (with shopId) is the UI layer's responsibility.
// ---------------------------------------------------------------------------

export const ActionRoute = Object.freeze({
  DIAGNOSTICS: 'diagnostics',
  WIZARD_WELCOME: 'wizard:welcome',
  WIZARD_EXPERIENCE: 'wizard:experience',
  WIZARD_THEME: 'wizard:theme',
  WIZARD_BUILD: 'wizard:build',
  INTEGRATION_SETTINGS: 'integration-settings',
  HOME_ACTIVATE: 'home:activate',
  HOME_INDEX: 'home:index',
  HOME_OPEN_PREVIEW: 'home:open-preview',
  HOME_PUBLISH: 'home:publish',
});

// ---------------------------------------------------------------------------
// Mode support matrix (rev1 launch)
// ---------------------------------------------------------------------------

export const MODE_SUPPORT = Object.freeze({
  [StorefrontMode.WIDGET]: {
    supported: true,
    recommended: true,
    shownInOnboarding: true,
  },
  [StorefrontMode.CLASSIC]: {
    supported: true,
    recommended: false,
    shownInOnboarding: true,
  },
  [StorefrontMode.COMBINED]: {
    supported: true,
    recommended: false,
    shownInOnboarding: true,
  },
});

// ---------------------------------------------------------------------------
// Preview feedback staleness threshold (doc 07 §4)
// ---------------------------------------------------------------------------

export const PREVIEW_FEEDBACK_STALE_MS = 15 * 60 * 1000; // 15 minutes

// ---------------------------------------------------------------------------
// Indexing staleness threshold (doc 36 §5)
// ---------------------------------------------------------------------------

export const INDEXING_STALE_MS = 2 * 60 * 1000; // 2 minutes

// ---------------------------------------------------------------------------
// Default / empty operator state
// ---------------------------------------------------------------------------

export const EMPTY_OPERATOR_STATE = Object.freeze({
  connection: ConnectionStatus.NOT_CONNECTED,
  indexing: IndexingStatus.IDLE,
  mode: null,
  previewBuild: PreviewBuildStatus.UNKNOWN,
  previewBrowser: PreviewBrowserStatus.UNKNOWN,
  preview: PreviewBuildStatus.UNKNOWN, // backward compat alias = previewBuild
  live: LiveStatus.UNKNOWN,
  integrationHealth: IntegrationHealth.UNKNOWN,
  diagnosticsSeverity: DiagnosticsSeverity.NONE,
  nextAction: null,
  publishState: null,
});
